// Feedback Learning Pipeline Agent UI (Polished)
// Additions: learning velocity trend, correction pattern chart, summary strip,
// A/B variant comparison chart.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, TextField,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, TrendChart, SimpleBarChart, RadialGauge,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    flIngest, flCorrectionPatterns, flFewShot, flAbPrompt, flKnowledgeUpdate, flLearningVelocity,
} from './api';

const TABS = ['Correction Ingestion', 'Pattern Analysis', 'Few-Shot Generator', 'A/B Optimizer', 'Knowledge Base', 'Learning Velocity'];

export default function FeedbackLearningAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [corrType, setCorrType] = useState('recategorize');
    const [origVal, setOrigVal] = useState('');
    const [corrVal, setCorrVal] = useState('');
    const [ingestResult, setIngestResult] = useState(null);
    const [patterns, setPatterns] = useState(null);
    const [fewShot, setFewShot] = useState(null);
    const [abData, setAbData] = useState(null);
    const [kbCat, setKbCat] = useState('');
    const [kbKey, setKbKey] = useState('');
    const [kbVal, setKbVal] = useState('');
    const [kbResult, setKbResult] = useState(null);
    const [velocity, setVelocity] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const load = async () => {
            if (tab === 1 && !patterns) { const r = await run(() => flCorrectionPatterns()); if (r?.data?.success) setPatterns(r.data.data); }
            else if (tab === 3 && !abData) { const r = await run(() => flAbPrompt()); if (r?.data?.success) setAbData(r.data.data); }
            else if (tab === 5 && !velocity) { const r = await run(() => flLearningVelocity()); if (r?.data?.success) setVelocity(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'Total Corrections', value: patterns?.total_corrections ?? '—', accent: 'primary' },
        { label: 'Auto-Rule Candidates', value: patterns?.auto_rule_candidates?.length ?? '—', accent: 'warning' },
        { label: 'Current Correction Rate', value: velocity?.current_correction_rate != null ? `${velocity.current_correction_rate}%` : '—', accent: 'info' },
        { label: 'Learning Trend', value: velocity?.trend || '—', accent: velocity?.trend === 'IMPROVING' ? 'success' : velocity?.trend === 'DEGRADING' ? 'danger' : 'neutral' },
    ];

    const renderTab = () => {
        switch (tab) {
            case 0:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Correction Ingestion" subtitle="Capture user corrections for learning" />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={3}>
                                <TextField size="small" label="Type" value={corrType} onChange={e => setCorrType(e.target.value)} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField size="small" label="Original" value={origVal} onChange={e => setOrigVal(e.target.value)} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField size="small" label="Corrected" value={corrVal} onChange={e => setCorrVal(e.target.value)} fullWidth />
                            </Grid>
                            <Grid item xs={12} md={1}>
                                <Button variant="contained" size="small" disabled={!corrVal || loading} startIcon={<PlayArrowIcon />}
                                    onClick={async () => {
                                        const r = await run(() => flIngest(corrType, origVal, corrVal));
                                        if (r?.data?.success) setIngestResult(r.data.data);
                                    }}>Ingest</Button>
                            </Grid>
                        </Grid>
                        {ingestResult && (
                            <Paper variant="outlined" sx={{ p: 1.5, borderLeft: '3px solid', borderColor: 'success.main' }}>
                                <Stack direction="row" spacing={1}>
                                    <Chip size="small" color="success" label="Ingested" />
                                    <Chip size="small" label={`Count: ${ingestResult.correction_count}`} />
                                    {ingestResult.auto_rule_eligible && <Chip size="small" color="warning" label="Auto-rule eligible (5+)" />}
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                );
            case 1:
                return patterns ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Correction Pattern Analysis" subtitle="Identify systematic correction patterns" />
                        <AIAnalysisBox text={patterns.ai_analysis} title="AI Pattern Analysis" />
                        {(patterns.repeated_patterns || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Top Repeated Patterns</Typography>
                                <SimpleBarChart
                                    data={patterns.repeated_patterns.slice(0, 8).map(p => ({
                                        pattern: `${p.original?.substring(0, 15)}→${p.corrected?.substring(0, 15)}`,
                                        count: p.count,
                                    }))}
                                    xKey="pattern"
                                    series={[{ key: 'count', label: 'Count', color: CHART_COLORS.warning }]}
                                    height={240}
                                    layout="vertical"
                                    showLegend={false}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'type', label: 'Type' },
                                { key: 'original', label: 'Original', render: r => r.original?.substring(0, 60) },
                                { key: 'corrected', label: 'Corrected', render: r => r.corrected?.substring(0, 60) },
                                { key: 'count', label: 'Count', align: 'right', render: r => <Chip size="small" color={r.count >= 5 ? 'warning' : 'default'} label={r.count} /> },
                            ]}
                            rows={patterns.repeated_patterns || []}
                            maxHeight={380}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 2:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Few-Shot Example Generator" subtitle="Convert corrections into training examples" />
                        <Button variant="contained" size="small" disabled={loading} startIcon={<PlayArrowIcon />} sx={{ alignSelf: 'flex-start' }}
                            onClick={async () => {
                                const r = await run(() => flFewShot('categorization', 10));
                                if (r?.data?.success) setFewShot(r.data.data);
                            }}>Generate</Button>
                        {fewShot && (
                            <>
                                <Chip size="small" label={`${fewShot.example_count} examples generated`} />
                                <AIAnalysisBox text={fewShot.formatted_examples} title="Formatted Few-Shot Prompt" />
                            </>
                        )}
                    </Stack>
                );
            case 3:
                return abData ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="A/B Prompt Optimizer" subtitle="Compare prompt variants" />
                        {(abData.variants || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Variant Comparison</Typography>
                                <SimpleBarChart
                                    data={abData.variants.map(v => ({
                                        variant: `Def ${v.agent_definition_id}`,
                                        success: v.success_rate,
                                        confidence: v.avg_confidence * 100,
                                    }))}
                                    xKey="variant"
                                    series={[
                                        { key: 'success', label: 'Success %', color: CHART_COLORS.success },
                                        { key: 'confidence', label: 'Confidence %', color: CHART_COLORS.secondary },
                                    ]}
                                    height={240}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'agent_definition_id', label: 'Agent Def' },
                                { key: 'total_runs', label: 'Runs', align: 'right' },
                                { key: 'success_rate', label: 'Success %', align: 'right', render: r => `${r.success_rate}%` },
                                { key: 'avg_confidence', label: 'Avg Conf', align: 'right' },
                                { key: 'avg_cost_usd', label: 'Avg Cost', align: 'right', render: r => `$${r.avg_cost_usd}` },
                                { key: 'winner', label: 'Winner', render: r => r.agent_definition_id === abData.recommended_winner && <Chip size="small" color="success" label="Winner" /> },
                            ]}
                            rows={abData.variants || []}
                            rowKeyField="agent_definition_id"
                        />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 4:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Knowledge Base Updates" subtitle="Add/update business knowledge for agents" />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={3}><TextField size="small" label="Category" value={kbCat} onChange={e => setKbCat(e.target.value)} fullWidth /></Grid>
                            <Grid item xs={12} md={3}><TextField size="small" label="Key" value={kbKey} onChange={e => setKbKey(e.target.value)} fullWidth /></Grid>
                            <Grid item xs={12} md={5}><TextField size="small" label="Value" value={kbVal} onChange={e => setKbVal(e.target.value)} fullWidth /></Grid>
                            <Grid item xs={12} md={1}>
                                <Button variant="contained" size="small" disabled={!kbCat || !kbKey || !kbVal || loading} startIcon={<PlayArrowIcon />}
                                    onClick={async () => {
                                        const r = await run(() => flKnowledgeUpdate(kbCat, kbKey, kbVal));
                                        if (r?.data?.success) setKbResult(r.data.data);
                                    }}>Update</Button>
                            </Grid>
                        </Grid>
                        {kbResult && (
                            <Chip size="small" color={kbResult.updated ? 'success' : 'warning'}
                                label={kbResult.updated ? `Updated KB #${kbResult.kb_id}` : kbResult.reason} />
                        )}
                    </Stack>
                );
            case 5:
                return velocity ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Learning Velocity" subtitle="Track how quickly agents improve" />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600}>Current Accuracy</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <RadialGauge
                                            value={100 - (velocity.current_correction_rate || 0)}
                                            label={velocity.meets_target ? 'On Target' : 'Needs Work'}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>Correction Rate Trend (%) — lower is better</Typography>
                                    <TrendChart
                                        data={(velocity.periods || []).map(p => ({ period: p.label, rate: p.correction_rate_pct }))}
                                        xKey="period"
                                        series={[{ key: 'rate', label: 'Correction Rate %', color: CHART_COLORS.primary }]}
                                        height={240}
                                        type="area"
                                    />
                                </Paper>
                            </Grid>
                        </Grid>
                        <DrillTable
                            columns={[
                                { key: 'label', label: 'Period' },
                                { key: 'dates', label: 'Dates', render: r => `${r.start} — ${r.end}` },
                                { key: 'total_runs', label: 'Runs', align: 'right' },
                                { key: 'corrections', label: 'Corrections', align: 'right' },
                                { key: 'correction_rate_pct', label: 'Rate %', align: 'right', render: r => `${r.correction_rate_pct}%` },
                            ]}
                            rows={velocity.periods || []}
                            rowKeyField="label"
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Feedback Learning Pipeline"
            subtitle="Agentic Learning — corrections, patterns, few-shot, A/B optimizer, KB, velocity."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
